import * as React from 'react';
import { Alert, Form, Input, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import OWButton from '../ow_button/OWButton';
import BackButton from '../back_button/BackButton';
import {
	TemplateDeliverableListFields,
	TemplateItemAddButton,
	TemplateItemRow,
	TemplateTaskListFields,
} from './ProjectTemplateItemFields';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';

const FIELD_TYPE_OPTIONS = [
	{ value: 'string', label: 'Text' },
	{ value: 'number', label: 'Number' },
	{ value: 'currency', label: 'Currency' },
	{ value: 'date', label: 'Date' },
];

// A row the user added and left blank is dropped rather than saved as an empty
// definition (the pre-antd-v4 form filtered the same way). These rows carry no
// required rule for that reason — validation would block the save before this could
// run, which is the same choice the deliverable and task lists make.
const cleanFieldDefinitions = (fieldDefinitions) =>
	(fieldDefinitions || [])
		.filter((field) => nullSafeGet('key', field) && nullSafeGet('type', field))
		.map((field) => ({ key: field.key, type: field.type }));

// A template task is title-only: no assignee, due date or ordering id. Array
// position IS the ordering, so reordering a list later is a plain splice.
const cleanTaskTemplates = (tasks) =>
	(tasks || []).filter((task) => nullSafeGet('title', task)).map((task) => ({ title: task.title }));

const cleanDeliverableTemplates = (deliverables) =>
	(deliverables || [])
		.filter((deliverable) => nullSafeGet('name', deliverable))
		.map((deliverable) => ({
			name: deliverable.name,
			description: deliverable.description,
			budget: deliverable.budget,
			tasks: cleanTaskTemplates(deliverable.tasks),
		}));

// The template's custom field definitions ({ key, type }) — one Form.List per
// required / optional group.
const FieldDefinitionListFields = ({ name, addLabel }) => (
	<Form.List name={name}>
		{(fields, { add, remove }) => (
			<>
				{fields.length > 0 ? (
					<div className="flex items-center gap-4">
						<div className="w-4" />
						<div className="footnoteregular text-character-secondary" style={{ width: 200 }}>
							Name
						</div>
						<div className="footnoteregular text-character-secondary" style={{ width: 120 }}>
							Type
						</div>
					</div>
				) : null}
				{fields.map(({ key, name: fieldName, ...restField }, index) => (
					<TemplateItemRow key={key} index={index} onRemove={() => remove(fieldName)}>
						<div className="flex items-start gap-4">
							<Form.Item
								{...restField}
								name={[fieldName, 'key']}
								className="mb-2"
								style={{ width: 200 }}
							>
								<Input />
							</Form.Item>
							<Form.Item {...restField} name={[fieldName, 'type']} className="mb-2">
								<Select style={{ width: 120 }} options={FIELD_TYPE_OPTIONS} />
							</Form.Item>
						</div>
					</TemplateItemRow>
				))}
				<TemplateItemAddButton label={addLabel} onClick={() => add({ type: 'string' })} />
			</>
		)}
	</Form.List>
);

const ProjectTemplateForm = ({
	formData,
	history,
	redirectForwardUrl,
	onSuccess,
	createErrors,
	updateErrors,
	creating,
	updating,
	currentUser,
	create,
	update,
}) => {
	const isUpdate = formData && formData.id !== undefined;
	const submitAction = isUpdate ? update : create;
	const submitText = isUpdate ? 'Update project template' : 'Create project template';
	const isLoading = updating || creating;
	// Deliverables / tasks on a template are part of projects-v2; unflagged tenants
	// see exactly the form they see today.
	const projectsV2 = isProjectsV2Enabled();
	const currencyId = nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD');

	const handleFinish = (values) => {
		const projectTemplate = {
			...formData,
			id: nullSafeGet('id', formData),
			name: values.name,
			buyerFacilityId: nullSafeGetOrElse(
				'buyerFacilityId',
				formData,
				nullSafeGet('facility.id', currentUser)
			),
			requiredFields: cleanFieldDefinitions(values.requiredFields),
			optionalFields: cleanFieldDefinitions(values.optionalFields),
			// When the flag is off the fields aren't rendered, so whatever a flagged
			// user already authored rides along untouched via the formData spread.
			...(projectsV2
				? {
						deliverableTemplates: cleanDeliverableTemplates(values.deliverableTemplates),
						taskTemplates: cleanTaskTemplates(values.taskTemplates),
				  }
				: {}),
		};

		submitAction(nullSafeGet('userType', currentUser), projectTemplate)
			.then((record) => {
				if (redirectForwardUrl) {
					history.push(redirectForwardUrl);
				}
				if (onSuccess) {
					onSuccess(record);
				}
			})
			// The failure is already surfaced by the create/update error Alert above the
			// form — swallow it here so it isn't also an unhandled rejection.
			.catch(() => {});
	};

	return (
		<Form
			layout="vertical"
			onFinish={handleFinish}
			className="supplierForm"
			initialValues={{
				name: nullSafeGet('name', formData),
				requiredFields: nullSafeGetOrElse('requiredFields', formData, []),
				optionalFields: nullSafeGetOrElse('optionalFields', formData, []),
				deliverableTemplates: nullSafeGetOrElse('deliverableTemplates', formData, []),
				taskTemplates: nullSafeGetOrElse('taskTemplates', formData, []),
			}}
		>
			{createErrors.length > 0 ? (
				<Form.Item>
					<Alert message={createErrors.join(' ')} type="error" />
				</Form.Item>
			) : null}
			{updateErrors.length > 0 ? (
				<Form.Item>
					<Alert message={updateErrors.join(' ')} type="error" />
				</Form.Item>
			) : null}
			<Form.Item label="Name" name="name">
				<Input style={{ maxWidth: 300 }} />
			</Form.Item>
			<Form.Item label="Required Fields" className="mb-4">
				<FieldDefinitionListFields name="requiredFields" addLabel="Add required field" />
			</Form.Item>
			<Form.Item label="Optional Fields" className="mb-4">
				<FieldDefinitionListFields name="optionalFields" addLabel="Add optional field" />
			</Form.Item>
			{projectsV2 ? (
				<>
					<Form.Item
						label="Deliverables"
						className="mb-4"
						tooltip={{
							title:
								'Every project created from this template starts with these deliverables. Due dates and assignees are set on the project.',
							icon: <InfoCircleOutlined />,
						}}
					>
						<TemplateDeliverableListFields name="deliverableTemplates" currencyId={currencyId} />
					</Form.Item>
					<Form.Item
						label="Tasks"
						className="mb-4"
						tooltip={{
							title: 'Tasks that belong to the project itself rather than to a deliverable.',
							icon: <InfoCircleOutlined />,
						}}
					>
						<TemplateTaskListFields name="taskTemplates" addLabel="Add task" />
					</Form.Item>
				</>
			) : null}
			<Form.Item>
				<OWButton
					type="primary"
					htmlType="submit"
					loading={isLoading}
					className="contactForm__button"
				>
					{submitText}
				</OWButton>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	formData: ownProps.formData,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,

	createErrors: state.project_templates.createErrors,
	updateErrors: state.project_templates.updateErrors,
	creating: state.project_templates.creating,
	updating: state.project_templates.updating,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	update: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.update(entity)
				: projectTemplatesRestCrudThunksForBuyer.update(entity)
		),
	create: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.create(entity)
				: projectTemplatesRestCrudThunksForBuyer.create(entity)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ProjectTemplateForm)
);
